import { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { ASSISTANT } from "@/constants/testIds";
import { toast } from "sonner";
import { Bot, Send, X, Check, Loader2, Sparkles, ArrowRight } from "lucide-react";

const SUGGESTIONS = [
  "Show me hot leads",
  "How many trials expire in 12 hours?",
  "How is Aarav Sharma doing?",
  "Send Aarav a pricing message",
];

export default function AssistantPanel({ open, onClose }) {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi 👋 I'm Pippy, your AI supervisor. Ask me about users, funnel, or tell me to send/tag/pause. Writes need your confirmation." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    setTimeout(() => scrollRef.current?.scrollTo({ top: 999999, behavior: "smooth" }), 100);
  }, [messages, pending]);

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setLoading(true);
    setPending(null);
    try {
      const { data } = await api.post("/assistant/query", { query: q });
      setMessages((m) => [
        ...m,
        { role: "assistant", text: data.reply || "…", data: data.data },
      ]);
      if (data.action) setPending(data.action);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: `Error: ${e?.response?.data?.detail || e.message}` }]);
    } finally {
      setLoading(false);
    }
  };

  const confirmAction = async () => {
    if (!pending) return;
    setLoading(true);
    try {
      const { data } = await api.post("/assistant/confirm", { action: pending.name, args: pending.args });
      if (data.ok) {
        toast.success("Done");
        setMessages((m) => [...m, { role: "assistant", text: `✅ Action completed: ${pending.preview || pending.name}` }]);
      } else {
        toast.error(data.error || "Action failed");
        setMessages((m) => [...m, { role: "assistant", text: `❌ ${data.error || "Failed"}` }]);
      }
    } catch (e) {
      toast.error("Failed");
    } finally {
      setPending(null);
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end pointer-events-none">
      <div
        className="w-full max-w-[440px] h-full bg-white border-l border-slate-200 shadow-xl flex flex-col pointer-events-auto"
        data-testid={ASSISTANT.panel}
      >
        <div className="border-b border-slate-200 px-5 py-4 flex items-center justify-between bg-gradient-to-br from-indigo-50 to-white">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-slate-900 flex items-center justify-center">
              <Bot className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="font-heading font-bold text-sm tracking-tight">Ask Pippy</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">AI Supervisor · Claude Sonnet 4.5</div>
            </div>
          </div>
          <button
            data-testid={ASSISTANT.closeBtn}
            onClick={onClose}
            className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors duration-150"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`${m.role === "user" ? "bg-slate-900 text-white" : "bg-indigo-50 text-slate-900 border border-indigo-100"} rounded-lg px-3 py-2 max-w-[90%]`}>
                <div className="text-sm whitespace-pre-wrap">{m.text}</div>
                {m.data && Object.keys(m.data).length > 0 && (
                  <div className="mt-2 pt-2 border-t border-indigo-200 space-y-1">
                    {Object.entries(m.data).map(([k, v]) => (
                      <details key={k} className="text-[11px]">
                        <summary className="font-mono cursor-pointer text-indigo-700">{k}</summary>
                        <pre className="mt-1 bg-white border border-slate-200 rounded p-2 overflow-x-auto text-[10px] max-h-40">{JSON.stringify(v, null, 2)}</pre>
                      </details>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex items-center gap-2 text-xs text-slate-500 pl-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> thinking…
            </div>
          )}
          {pending && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <div className="flex items-start gap-2 mb-2">
                <Sparkles className="h-4 w-4 text-orange-600 mt-0.5" />
                <div className="flex-1">
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-orange-700">Proposed action</div>
                  <div className="text-sm font-semibold text-slate-900 mt-1">{pending.name}</div>
                  <div className="text-xs text-slate-700 mt-1">{pending.preview}</div>
                  {pending.reason && (
                    <div className="text-[11px] text-slate-500 italic mt-1">Reason: {pending.reason}</div>
                  )}
                </div>
              </div>
              <div className="flex gap-2 mt-2">
                <button
                  data-testid={ASSISTANT.confirmBtn}
                  onClick={confirmAction}
                  disabled={loading}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold bg-orange-600 text-white px-3 py-1.5 rounded-md hover:bg-orange-500 transition-colors duration-150 disabled:opacity-60"
                >
                  <Check className="h-3 w-3" /> Confirm & Execute
                </button>
                <button
                  data-testid={ASSISTANT.cancelBtn}
                  onClick={() => setPending(null)}
                  className="text-xs px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 transition-colors duration-150"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Suggestions */}
        {messages.length <= 1 && (
          <div className="px-4 pb-3 flex flex-wrap gap-1.5">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                onClick={() => send(s)}
                className="text-[11px] px-2 py-1 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors duration-150"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <div className="border-t border-slate-200 p-3 flex items-end gap-2">
          <textarea
            data-testid={ASSISTANT.input}
            rows={2}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
            }}
            placeholder="Ask about a user, or say 'send Aarav a pricing message…'"
            className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-indigo-500 outline-none transition-colors duration-150 resize-none"
          />
          <button
            data-testid={ASSISTANT.sendBtn}
            onClick={() => send()}
            disabled={loading || !input.trim()}
            className="p-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-500 transition-colors duration-150 disabled:opacity-60"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
